import type { CoreHousehold } from '../types/financial';
import type { OnboardingAsset, OnboardingDraft } from '../types/onboarding';
import {
  portfolioAccountsToCore,
  portfolioAccountsToDraftAssets,
  type PortfolioImportResponse,
} from '../services/portfolioImportService';
import {
  observationsFromPortfolioImport,
  resolveAsOfDate,
  upsertBalanceObservations,
} from './balanceObservations';
import { stripSampleInvestments } from './householdFromDraft';

/**
 * Merge extracted rows into existing investments.
 * Same label + same balance → refresh; same label + different balance → keep both
 * (Wealthsimple RRSP + pension RRSP).
 */
function mergeDraftInvestments(
  existing: OnboardingAsset[],
  imported: OnboardingAsset[],
): OnboardingAsset[] {
  const nonInvest = existing.filter((a) => !a.isInvestment);
  const investments = existing.filter((a) => a.isInvestment);
  const next = [...investments];
  for (const row of imported) {
    const matchIndex = next.findIndex(
      (a) =>
        a.label.trim().toLowerCase() === row.label.trim().toLowerCase() &&
        Math.abs(a.value - row.value) < 0.02,
    );
    if (matchIndex >= 0) {
      next[matchIndex] = { ...next[matchIndex], ...row, isInvestment: true };
    } else {
      next.push({ ...row, isInvestment: true });
    }
  }
  return [...nonInvest, ...next];
}

function mergeInvestmentAccounts(
  existing: import('../types/financial').CoreAccount[],
  imported: import('../types/financial').CoreAccount[],
) {
  const nonInvest = existing.filter(
    (a) => !(a.kind === 'asset' && a.isInvestment),
  );
  const investments = existing.filter(
    (a) => a.kind === 'asset' && a.isInvestment && !a.id.startsWith('asset_'),
  );
  const next = [...investments];
  for (const row of imported) {
    const matchIndex = next.findIndex(
      (a) =>
        a.label.trim().toLowerCase() === row.label.trim().toLowerCase() &&
        Math.abs(a.balance - row.balance) < 0.02,
    );
    if (matchIndex >= 0) {
      next[matchIndex] = {
        ...next[matchIndex],
        ...row,
        id: next[matchIndex].id,
        kind: 'asset',
        isInvestment: true,
      };
    } else {
      next.push(row);
    }
  }
  return stripSampleInvestments([...nonInvest, ...next]);
}

export type AppliedPortfolioImport = {
  accountCount: number;
  totalBalance: number;
  broker: string | null;
  /** Snapshot date written to `balanceObservations` (`YYYY-MM-DD`). */
  asOfDate: string;
  nextDraftAssets: OnboardingAsset[];
  nextCore: CoreHousehold;
};

/** Merge a portfolio extract response into draft assets + household accounts. */
export function applyPortfolioImportResult(params: {
  result: PortfolioImportResponse;
  draftAssets: OnboardingAsset[];
  core: CoreHousehold;
}): AppliedPortfolioImport | null {
  if (!params.result.accounts.length) return null;

  const draftAssets = portfolioAccountsToDraftAssets(params.result.accounts);
  const coreAccounts = portfolioAccountsToCore(params.result.accounts);
  const nextAccounts = mergeInvestmentAccounts(params.core.accounts, coreAccounts);
  const asOfDate = resolveAsOfDate(params.result.asOfDate);
  const incomingObservations = observationsFromPortfolioImport({
    mergedAccounts: nextAccounts,
    importedAccounts: coreAccounts,
    asOfDate,
  });

  const userInvestmentIds = new Set(
    nextAccounts
      .filter((a) => a.kind === 'asset' && a.isInvestment)
      .map((a) => a.id),
  );
  const priorObservations = params.core.balanceObservations.filter((o) =>
    userInvestmentIds.has(o.accountId),
  );

  return {
    accountCount: params.result.accounts.length,
    totalBalance: nextAccounts
      .filter((a) => a.kind === 'asset' && a.isInvestment)
      .reduce((sum, a) => sum + a.balance, 0),
    broker: params.result.broker,
    asOfDate,
    nextDraftAssets: mergeDraftInvestments(params.draftAssets, draftAssets),
    nextCore: {
      ...params.core,
      accounts: nextAccounts,
      balanceObservations: upsertBalanceObservations(
        priorObservations,
        incomingObservations,
      ),
      profile: {
        ...params.core.profile,
        householdName:
          params.core.profile.householdName === 'Sample Household'
            ? 'Your household'
            : params.core.profile.householdName,
      },
    },
  };
}

export function draftPatchFromPortfolio(
  applied: AppliedPortfolioImport,
): Partial<OnboardingDraft> {
  return {
    assets: applied.nextDraftAssets,
    completedIntro: true,
  };
}
